import React, { useEffect, useRef, useCallback } from 'react';
import { 
  Clock, 
  MapPin, 
  Phone, 
  Utensils, 
  ShoppingBag, 
  Users, 
  MessageCircle, 
  Sparkles, 
  Star,
  Home as HomeIcon, 
  Info, 
  Menu as MenuIcon, 
  X,
  ChevronDown,
  LayoutDashboard
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from "../../context/AuthContext";
import { supabase } from "../../lib/supabaseClient";
import Atropos from 'atropos';

// InteractiveCard Component with Continuous Light Movement via CSS Transform
interface InteractiveCardProps {
  children: React.ReactNode;
  className?: string;
}

const InteractiveCard = React.memo(({ children, className = '' }: InteractiveCardProps) => {
  const atroposRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null); // Ref for the inner element

  useEffect(() => {
    if (!atroposRef.current) return;

    const atroposInstance = Atropos({
      el: atroposRef.current,
      activeOffset: 40,
      shadowScale: 1.05,
      rotateXMax: 15,
      rotateYMax: 15,
      highlight: true,
      shadow: true,
    });

    let time = 0;
    let animationFrameId: number;

    const animate = () => {
      if (!innerRef.current) return;
      const rotateX = Math.sin(time * 0.5) * 2;
      const rotateY = Math.cos(time * 0.5) * 2;
      if (!atroposRef.current?.matches(':hover')) {
        innerRef.current.style.transform = `rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;
      } else {
        innerRef.current.style.transform = ''; // Reset when hovered
      }
      time += 0.02;
      animationFrameId = requestAnimationFrame(animate);
    };

    animationFrameId = requestAnimationFrame(animate);

    return () => {
      atroposInstance.destroy();
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <div ref={atroposRef} className={`atropos ${className}`}>
      <div className="atropos-scale">
        <div className="atropos-rotate">
          <div ref={innerRef} className="atropos-inner">{children}</div>
        </div>
      </div>
    </div>
  );
});

// Logo Component
const Logo = React.memo(() => (
  <div className="inline-flex flex-col items-center bg-white rounded-xl shadow-lg p-8">
    <div className="flex items-center gap-4">
      <div className="relative">
        <span
          role="img"
          aria-label="leaf"
          title="Decorative leaf icon"
          className="text-4xl absolute -top-3 -left-2 transform rotate-45 text-emerald-600 opacity-80"
        >
          🌿
        </span>
        <div className="text-6xl font-serif tracking-tight flex items-baseline">
          <span className="text-emerald-800">VG</span>
          <span className="text-black text-5xl ml-1">Foods</span>
        </div>
      </div>
    </div>
    <div className="mt-2 text-sm font-medium tracking-widest text-black uppercase">Vegan, Veg & Non-Veg</div>
    <div className="mt-1 text-sm font-medium tracking-widest text-black">சுவையான வீட்டுச் சமையல்</div>
    <div className="mt-1 text-xs text-gray-600 text-center">Est. 2025 • London</div>
  </div>
));

// OrderCard Component
interface OrderCardProps {
  title: string;
  image: string; // This will now be a URL string, not an imported module
  icon: React.ElementType;
  description: string;
}

const OrderCard = React.memo(({ title, image, icon: Icon, description }: OrderCardProps) => {
  const navigate = useNavigate();

  const handleOrderClick = useCallback(() => {
    const lowerTitle = title.toLowerCase();
    switch (lowerTitle) {
      case 'dine in': return navigate('/dine-in');
      case 'takeaway': return navigate('/takeaway');
      case 'party orders': return navigate('/party-order');
      case 'whatsapp order': return window.open('https://wa.me/447521262119', '_blank');
      default: return navigate('/menu');
    }
  }, [navigate, title]);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden transition-all hover:shadow-xl sm:transform sm:hover:scale-105">
      <div className="flex flex-col sm:block h-full">
        <div className="h-24 sm:h-48 overflow-hidden">
          <img
            src={image} // Use the image URL directly
            alt={title}
            title={`Image of ${title}`}
            className="w-full h-full object-cover transition-transform duration-500 sm:hover:scale-110"
          />
        </div>
        <div className="p-3 sm:p-4 flex flex-col justify-between flex-grow">
          <div>
            <div className="flex items-center mb-1 sm:mb-2">
              <Icon className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-600 mr-2" />
              <h4 className="font-serif text-sm sm:text-base font-semibold text-black dark:text-white">{title}</h4>
            </div>
            <p className="font-inter text-gray-700 dark:text-gray-300 text-xs sm:text-sm line-clamp-2">{description}</p>
          </div>
          <button
            onClick={handleOrderClick}
            title={`Order ${title} now`}
            className="font-inter mt-2 sm:mt-3 w-full bg-emerald-700 hover:bg-emerald-800 text-white text-xs sm:text-sm font-medium py-1 sm:py-2 px-3 sm:px-4 rounded-full transition-colors duration-300"
          >
            Order Now
          </button>
        </div>
      </div>
    </div>
  );
});

// Sidebar Component
interface SidebarProps {
  className?: string;
}

const Sidebar: React.FC<SidebarProps> = ({ className }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [isDiningOpen, setIsDiningOpen] = React.useState(false);
  const [isAdmin, setIsAdmin] = React.useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  const toggleSidebar = useCallback(() => setIsOpen(prev => !prev), []);
  const toggleDining = useCallback(() => setIsDiningOpen(prev => !prev), []);

  useEffect(() => {
    if (!user) return;

    const fetchUserRole = async () => {
      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("role")
          .eq("id", user.id)
          .single();
        if (error) throw error;
        setIsAdmin(data?.role === "admin");
      } catch (error) {
        console.error("Error fetching user role:", error);
      }
    };

    fetchUserRole();
  }, [user]);

  const menuItems = React.useMemo(() => [
    { name: "Home", path: "/", icon: HomeIcon },
    { name: "About", path: "/about", icon: Info },
    { name: "Menu", path: "/menu", icon: MenuIcon },
    {
      name: "Dining & Orders",
      icon: Utensils,
      subItems: [
        { name: "Dine In", path: "/dine-in", icon: Utensils },
        { name: "Takeaway", path: "/takeaway", icon: ShoppingBag },
        { name: "Party Order", path: "/party-order", icon: Users },
        {
          name: "WhatsApp Order",
          path: "https://wa.me/9751903129?text=Hi,%20I%20would%20like%20to%20place%20an%20order.",
          icon: MessageCircle,
          isExternal: true,
        },
      ],
    },
    { name: "Contact", path: "/contact", icon: Phone },
    ...(isAdmin ? [{ name: "Admin Dashboard", path: "/admin", icon: LayoutDashboard }] : []),
  ], [isAdmin]);

  const handleNavigation = useCallback((path: string, isExternal?: boolean) => {
    if (isExternal) {
      window.open(path, '_blank', 'noopener,noreferrer');
    } else {
      navigate(path);
    }
    setIsOpen(false);
  }, [navigate]);

  return (
    <>
      <button
        className="fixed top-20 right-4 z-50 md:hidden p-2 bg-emerald-600 text-white rounded-md"
        title={isOpen ? "Close sidebar" : "Open sidebar"}
        onClick={toggleSidebar}
      >
        {isOpen ? (
          <X className="w-6 h-6" />
        ) : (
          <div className="space-y-1">
            <span className="block w-6 h-0.5 bg-white" />
            <span className="block w-6 h-0.5 bg-white" />
            <span className="block w-6 h-0.5 bg-white" />
          </div>
        )}
      </button>

      <div
        className={`fixed top-0 left-0 h-full w-64 bg-white dark:bg-gray-800 shadow-lg transform transition-transform duration-300 ease-in-out z-40 md:hidden ${isOpen ? "translate-x-0" : "-translate-x-full"} ${className || ""}`}
      >
        <button
          className="md:hidden absolute top-4 right-4 p-1 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
          title="Close sidebar"
          onClick={toggleSidebar}
        >
          <X className="w-6 h-6" />
        </button>

        <div className="p-4 h-full flex flex-col">
          <h1 className="text-xl font-serif font-bold text-gray-900 dark:text-white mb-6">VG Foods</h1>
          <nav className="flex-1">
            <ul className="space-y-2">
              {menuItems.map((item, index) => (
                <li key={index}>
                  {item.subItems ? (
                    <div>
                      <button
                        onClick={toggleDining}
                        title="Toggle dining options"
                        className="flex items-center w-full text-left p-2 rounded-lg hover:bg-emerald-100 dark:hover:bg-gray-700 transition-colors duration-200"
                      >
                        <item.icon className="w-6 h-6 text-emerald-600 mr-3" />
                        <span className="font-inter text-gray-900 dark:text-white font-medium flex-1">{item.name}</span>
                        <ChevronDown
                          className={`w-5 h-5 text-gray-600 transform transition-transform ${isDiningOpen ? "rotate-180" : ""}`}
                        />
                      </button>
                      {isDiningOpen && (
                        <ul className="pl-8 space-y-2 mt-2">
                          {item.subItems.map((subItem, subIndex) => (
                            <li key={subIndex}>
                              <button
                                onClick={() => handleNavigation(subItem.path, subItem.isExternal)}
                                title={`Navigate to ${subItem.name}`}
                                className="flex items-center w-full text-left p-2 rounded-lg hover:bg-emerald-100 dark:hover:bg-gray-700 transition-colors duration-200"
                              >
                                <subItem.icon className="w-5 h-5 text-emerald-600 mr-2" />
                                <span className="font-inter text-gray-900 dark:text-white text-sm">{subItem.name}</span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ) : (
                    <button
                      onClick={() => handleNavigation(item.path)}
                      title={`Navigate to ${item.name}`}
                      className="flex items-center w-full text-left p-2 rounded-lg hover:bg-emerald-100 dark:hover:bg-gray-700 transition-colors duration-200"
                    >
                      <item.icon className="w-6 h-6 text-emerald-600 mr-3" />
                      <span className="font-inter text-gray-900 dark:text-white font-medium">{item.name}</span>
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </div>

      {isOpen && (
        <div
          className="fixed inset-0 bg-black opacity-50 z-30 md:hidden"
          onClick={toggleSidebar}
          title="Close sidebar"
        />
      )}
    </>
  );
};

// Home Component
const Home = () => {
  const navigate = useNavigate();

  const orderOptions = React.useMemo(() => [
    {
      title: "Dine In",
      image: "/websiteimages/dinein.avif",
      icon: Utensils,
      description: "Enjoy our delicious meals in our cozy restaurant atmosphere."
    },
    {
      title: "Party Orders",
      image: "/websiteimages/partyorder.avif",
      icon: Users,
      description: "Special catering services for your events and celebrations."
    },
    {
      title: "Takeaway",
      image: "/websiteimages/Newtakeaway.jpg",
      icon: ShoppingBag,
      description: "Order ahead and pick up your dishes at your convenience."
    },
    {
      title: "WhatsApp Order",
      image: "/websiteimages/whatsapporderwhite.avif",
      icon: MessageCircle,
      description: "Quick and easy ordering through WhatsApp messaging."
    }
  ], []);

  return (
    <>
      <section
        id="home"
        className="py-16 sm:py-12 min-h-screen relative bg-no-repeat bg-center bg-cover md:bg-fixed"
        style={{
          backgroundImage: `url(/websiteimages/bg_desktop.webp)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-black opacity-60 md:opacity-50" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-8 sm:mb-12">
            <h1 className="font-serif text-2xl font-bold text-white mb-4 sm:text-3xl md:text-5xl">
              Welcome to <span className="text-emerald-600">VG Foods</span>
            </h1>
            <p className="font-inter text-base text-gray-300 mb-6 sm:text-lg">Vegan, Veg & Non-Veg</p>
            <InteractiveCard className="w-full max-w-md mx-auto">
              <div className="relative bg-gradient-to-br from-emerald-600/10 to-teal-600/10 backdrop-blur-sm p-8 rounded-2xl border border-white/10 shadow-2xl">
                <div className="absolute -top-4 -left-4">
                  <Sparkles className="w-8 h-8 text-emerald-400 animate-pulse" />
                </div>
                <div className="absolute -bottom-4 -right-4">
                  <Star className="w-8 h-8 text-teal-400 animate-pulse" />
                </div>
                <div className="relative z-10 space-y-8">
                  <Logo />
                  <div className="space-y-4">
                    <h2 className="text-xl font-serif font-medium text-white">Experience the Future of Food</h2>
                    <p className="font-inter text-gray-200">Discover a world of veg and non-veg delights, crafted with passion and innovation.</p>
                  </div>
                  <button 
                    onClick={() => navigate('/menu')}
                    title="View our menu"
                    className="font-inter w-full py-3 px-6 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-medium rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105"
                  >
                    Explore Menu
                  </button>
                </div>
              </div>
            </InteractiveCard>
          </div>

          <div className="mt-8 sm:mt-12 mb-12 sm:mb-16">
            <div className="text-center mb-6 sm:mb-12">
              <h2 className="font-serif text-lg font-extrabold text-white sm:text-xl lg:text-3xl">Order Your Way: Dine-In, Takeaway, Parties & More</h2>
              <p className="font-inter mt-2 text-sm text-gray-300 sm:mt-5 sm:text-lg sm:max-w-xl sm:mx-auto">Choose your preferred way to enjoy our delicious food</p>
            </div>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {orderOptions.map((option, index) => (
                <OrderCard
                  key={index}
                  title={option.title}
                  image={option.image}
                  icon={option.icon}
                  description={option.description}
                />
              ))}
            </div>
          </div>

          <div className="mt-12 sm:mt-20">
            <h2 className="font-serif text-2xl font-bold text-white text-center mb-6 sm:text-3xl sm:mb-8">Get in Touch</h2>
            <div className="grid grid-cols-3 gap-2 sm:gap-6 sm:grid-cols-3 max-w-4xl mx-auto">
              <div className="bg-white dark:bg-gray-800 p-3 sm:p-6 rounded-xl shadow-md flex flex-col items-center">
                <Phone className="w-5 h-5 sm:w-8 sm:h-8 text-emerald-600 mb-2 neutron sm:mb-4" />
                <h4 className="font-serif text-sm sm:text-lg font-semibold text-black dark:text-white mb-1 sm:mb-2 text-center">Contact Us</h4>
                <p className="font-inter text-gray-700 dark:text-gray-300 text-xs sm:text-base text-center">07521 262119</p>
                <p className="font-inter text-gray-700 dark:text-gray-300 text-xs sm:text-base text-center">07424 762470</p>
              </div>
              <div className="bg-white dark:bg-gray-800 p-3 sm:p-6 rounded-xl shadow-md flex flex-col items-center">
                <MapPin className="w-5 h-5 sm:w-8 sm:h-8 text-emerald-600 mb-2 sm:mb-4" />
                <h4 className="font-serif text-sm sm:text-lg font-semibold text-black dark:text-white mb-1 sm:mb-2 text-center">Location</h4>
                <p className="font-inter text-gray-700 dark:text-gray-300 text-xs sm:text-base text-center">855 Bristol Rd South, Northfield, Birmingham B31 2PA</p>
              </div>
              <div className="bg-white dark:bg-gray-800 p-3 sm:p-6 rounded-xl shadow-md flex flex-col items-center">
                <Clock className="w-5 h-5 sm:w-8 sm:h-8 text-emerald-600 mb-2 sm:mb-4" />
                <h4 className="font-serif text-sm sm:text-lg font-semibold text-black dark:text-white mb-1 sm:mb-2 text-center">Opening Hours</h4>
                <p className="font-inter text-gray-700 dark:text-gray-300 text-xs sm:text-base text-center">10 AM to 5 PM</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Sidebar />
    </>
  );
};

export default Home;